// src/hooks/useElectricityGenTS.js
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

const ELC_GEN_QUERY = gql`
  query ElcGen($scenario: String, $from: Int, $to: Int) {
    timeSeries(view: ELC_GEN, scenario: $scenario, fromYear: $from, toYear: $to) {
      year
      group
      value   # in PJ
    }
  }
`;

/**
 * Sums TAB_ELC-Gen by year and returns totals in **TWh**.
 * - PJ → TWh uses factor 0.277778 by default.
 * - Works for a range or a single year.
 *
 * Usage:
 *   // Range
 *   useElectricityGenTS({ scenario, from: 2025, to: 2050 })
 *
 *   // Single year (either form works)
 *   useElectricityGenTS({ scenario, year: 2025 })
 *   useElectricityGenTS({ scenario, from: 2025 }) // since to defaults to from
 */
export function useElectricityGenTS({
  scenario,
  year,                 // optional shorthand for a single year
  from = 2025,
  to,                   // if omitted, defaults to `from` (single-year)
  unitScale = 1, // PJ -> TWh
}) {
  // Decide the effective window
  const fromVar = year ?? from;
  const toVar   = year ?? (to ?? from);

  const { data, loading, error } = useQuery(ELC_GEN_QUERY, {
    variables: { scenario, from: fromVar, to: toVar },
    fetchPolicy: "cache-first",
  });

  const { labels, totalsByYear, singleYearTotalTWh } = useMemo(() => {
    const rows = data?.timeSeries ?? [];
    const byYear = new Map();

    for (const r of rows) {
      const y = Number(r.year);
      // Handles scientific notation strings like "7.8e-8"
      const vPJ = Number(r.value) || 0;
      const vTWh = vPJ * unitScale;
      byYear.set(y, (byYear.get(y) || 0) + vTWh);
    }

    const years = Array.from(byYear.keys()).sort((a, b) => a - b);
    const labels = years.map(String);
    const totalsByYear = years.map((y) => byYear.get(y) ?? 0);

    // Convenience for single-year requests
    const isSingle = fromVar === toVar;
    const singleYearTotalTWh = isSingle
      ? (totalsByYear.length ? totalsByYear[0] : 0)
      : null;

    return { labels, totalsByYear, singleYearTotalTWh };
  }, [data, unitScale, fromVar, toVar]);

  return { labels, totalsByYear, singleYearTotalTWh, loading, error };
}
